import { useEffect, useState } from 'react';
import { Alert, Button, Card, Modal, Space, Spin, Table, Tag, Tooltip, Typography } from 'antd';
import { MergeCellsOutlined } from '@ant-design/icons';
import { APIClient } from '../helpers/api_helper';
import MoneyDisplay from './MoneyDisplay';
import { TYPE_OPTS } from './IntentTypeSelect';
import utils from './utils';

const { Text, Paragraph } = Typography;
const api = new APIClient();

const MERGE_URL = '/api/intents/merge/';

const typeLabel = (type) => TYPE_OPTS.find(o => o.value === type)?.label || type;

/**
 * The list toolbar's merge button. `ids` is the selection as concrete ids
 * (useIntentSelection's `explicitIds`), null when it spans unloaded pages.
 */
export const IntentMergeButton = ({ ids, onClick }) => {
  const title = ids == null
    ? 'Združevanje deluje le na posamezno izbranih transakcijah'
    : ids.length < 2
      ? 'Združi izbrane (izberite vsaj dve)'
      : 'Združi izbrane';
  return (
    <Tooltip title={title}>
      <Button
        disabled={!ids || ids.length < 2}
        onClick={onClick}
        icon={<MergeCellsOutlined />}
      />
    </Tooltip>
  );
};

/**
 * Preview + confirm for folding the selected incomes/expenses into one
 * (backend: intents/merge.py). The preview is the server's own dry run, so the
 * total, the kept row and the description shown are exactly what gets written.
 *
 * When the merge isn't allowed the dry run still answers 200 with `errors`,
 * shown inline here -- a 400 would also raise the global error toast.
 */
const IntentMergeModal = ({ open, ids, onCancel, onMerged }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(false);

  // Keyed on the ids' content: the page hands in a fresh array every render.
  const idsKey = (ids || []).join(',');

  useEffect(() => {
    if (!open) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api.post(MERGE_URL, { ids, dry_run: true }, { successMsg: null })
      .then(resp => {
        if (cancelled) return;
        // null = 403; the error toast already said which permission is missing.
        if (!resp) onCancel();
        else setPreview(resp);
      })
      .catch(() => { if (!cancelled) onCancel(); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, idsKey]);

  const handleMerge = () => {
    setMerging(true);
    api.post(MERGE_URL, { ids }, { successMsg: 'Transakcije združene' })
      .then(resp => { if (resp) onMerged(resp.intent.id); })
      .catch(error => console.error(error))
      .finally(() => setMerging(false));
  };

  const columns = [
    {
      title: 'Datum',
      dataIndex: 'date_to_be_paid',
      width: 110,
      render: dt => utils.formatDt(dt),
    },
    {
      title: 'Naziv',
      dataIndex: 'title',
      render: (title, r) => (
        <Space size={6}>
          <span>{title}</span>
          {r.is_base && <Tag color="blue">ohrani se</Tag>}
        </Space>
      ),
    },
    {
      title: 'Znesek',
      dataIndex: 'amount',
      align: 'right',
      render: (amount, r) => <MoneyDisplay val={parseFloat(amount)} type={r.type} />,
    },
  ];

  const renderPreview = () => {
    if (!preview.can_merge) {
      return (
        <Alert
          type="error"
          showIcon
          message="Teh transakcij ni mogoče združiti"
          description={preview.errors.map(e => <div key={e}>{e}</div>)}
        />
      );
    }
    return (
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Table
          size="small"
          rowKey="id"
          pagination={false}
          columns={columns}
          dataSource={preview.sources}
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={2}>
                <Text strong>Rezultat: {typeLabel(preview.type)}</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} align="right">
                <MoneyDisplay val={parseFloat(preview.amount)} type={preview.type} />
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )}
        />
        <Text type="secondary">
          Transakcija »{preview.base_title}« se ohrani (z datumi, računom, projektom
          in kategorijo) in dobi skupni znesek, ostale bodo izbrisane.
        </Text>
        {preview.warnings.map(w => (
          <Alert key={w} type="warning" showIcon message={w} />
        ))}
        <div>
          <Text strong>Opis</Text>
          <Card size="small" style={{ marginTop: 4 }}>
            <Paragraph style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
              {preview.descr}
            </Paragraph>
          </Card>
        </div>
      </Space>
    );
  };

  return (
    <Modal
      title={`Združi ${ids?.length || 0} transakcij`}
      open={open}
      width={720}
      destroyOnClose
      okText="Združi"
      okButtonProps={{ disabled: !preview?.can_merge }}
      confirmLoading={merging}
      onOk={handleMerge}
      onCancel={merging ? undefined : onCancel}
      maskClosable={!merging}
    >
      {loading || !preview
        ? <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
        : renderPreview()}
    </Modal>
  );
};

export default IntentMergeModal;
